import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import axios from 'axios';
import {
  useNavigate,
  useParams
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import {
  FaArrowLeft,
  FaComment,
  FaEye,
  FaHeart,
  FaPause,
  FaPlay,
  FaPlus,
  FaRegHeart,
  FaTrash,
  FaVolumeMute,
  FaVolumeUp
} from 'react-icons/fa';

import '../css/UserMoments.css';

function UserMoments() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const observerRef = useRef(null);
  const cardRefs = useRef(new Map());
  const registeredViewsRef = useRef(new Set());

  const [moments, setMoments] = useState([]);
  const [profile, setProfile] = useState(null);

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const [mutedIds, setMutedIds] = useState(new Set());
  const [pausedIds, setPausedIds] = useState(new Set());

  const token = localStorage.getItem('authToken');

  const authorizationHeader = useMemo(() => {
    if (!token) return null;

    return token.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`;
  }, [token]);

  const api = useMemo(() => {
    return axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: authorizationHeader
        ? {
          Authorization: authorizationHeader
        }
        : {}
    });
  }, [authorizationHeader]);

  const loadMoments = useCallback(async () => {
    if (!authorizationHeader) {
      setErrorMessage(
        t(
          'moments.viewAuthenticationRequired',
          'Vous devez être connecté pour voir les Moments.'
        )
      );
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await api.get(
        `/api/moments/user/${userId}`,
        {
          params: {
            page: 0,
            size: 30
          }
        }
      );

      const payload = response.data;
      const content = Array.isArray(payload?.content)
        ? payload.content
        : Array.isArray(payload)
          ? payload
          : [];

      setMoments(content);

      if (content.length > 0) {
        const first = content[0];

        setProfile({
          userId: first.userId,
          userName: first.userName,
          userAge: first.userAge,
          userProfilePhoto:
            first.userProfilePhoto
        });
      }
    } catch (error) {
      console.error(
        '❌ User Moments error:',
        error
      );

      setErrorMessage(
        error.response?.data?.message ||
        t(
          'moments.userMomentsError',
          'Impossible de charger les Moments de cet utilisateur.'
        )
      );
    } finally {
      setLoading(false);
    }
  }, [api, authorizationHeader, t, userId]);

  useEffect(() => {
    document.body.classList.add('user-moments-body');

    loadMoments();

    return () => {
      document.body.classList.remove(
        'user-moments-body'
      );
    };
  }, [loadMoments]);

  const updateMoment = (
    momentId,
    changes
  ) => {
    setMoments((previous) =>
      previous.map((moment) =>
        moment.id === momentId
          ? {
            ...moment,
            ...changes
          }
          : moment
      )
    );
  };

  const registerView = useCallback(
    async (momentId) => {
      if (
        registeredViewsRef.current.has(momentId)
      ) {
        return;
      }

      registeredViewsRef.current.add(momentId);

      try {
        const response = await api.post(
          `/api/moments/${momentId}/view`
        );

        if (
          typeof response.data?.viewsCount ===
          'number'
        ) {
          updateMoment(momentId, {
            viewsCount:
              response.data.viewsCount
          });
        }
      } catch (error) {
        registeredViewsRef.current.delete(momentId);

        console.error(
          '❌ User Moment view error:',
          error
        );
      }
    },
    [api]
  );

  useEffect(() => {
    observerRef.current?.disconnect();

    observerRef.current =
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const momentId = Number(
              entry.target.dataset.momentId
            );

            const video =
              entry.target.querySelector('video');

            if (
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.65
            ) {
              registerView(momentId);

              if (
                video &&
                !pausedIds.has(momentId)
              ) {
                video.play().catch(() => { });
              }
            } else {
              video?.pause();
            }
          });
        },
        {
          threshold: [0.3, 0.65, 0.9]
        }
      );

    cardRefs.current.forEach((element) => {
      if (element) {
        observerRef.current.observe(element);
      }
    });

    return () =>
      observerRef.current?.disconnect();
  }, [moments, pausedIds, registerView]);

  const setCardRef = (
    momentId,
    element
  ) => {
    if (element) {
      cardRefs.current.set(
        momentId,
        element
      );
    } else {
      cardRefs.current.delete(momentId);
    }
  };

  const handleLike = async (moment) => {
    const previousLiked =
      Boolean(moment.likedByCurrentUser);

    const previousCount =
      Number(moment.likesCount) || 0;

    updateMoment(moment.id, {
      likedByCurrentUser:
        !previousLiked,
      likesCount: previousLiked
        ? Math.max(0, previousCount - 1)
        : previousCount + 1
    });

    try {
      const response = await api.post(
        `/api/moments/${moment.id}/like`
      );

      updateMoment(moment.id, {
        likedByCurrentUser:
          response.data?.liked ??
          !previousLiked,
        likesCount:
          response.data?.likesCount ??
          previousCount
      });
    } catch (error) {
      updateMoment(moment.id, {
        likedByCurrentUser:
          previousLiked,
        likesCount: previousCount
      });
    }
  };

  const handleDelete = async (momentId) => {
    const confirmed = window.confirm(
      t(
        'moments.deleteConfirm',
        'Supprimer ce Moment ?'
      )
    );

    if (!confirmed) return;

    try {
      await api.delete(
        `/api/moments/${momentId}`
      );

      setMoments((previous) =>
        previous.filter(
          (moment) =>
            moment.id !== momentId
        )
      );
    } catch (error) {
      window.alert(
        error.response?.data?.message ||
        t(
          'moments.deleteError',
          'Impossible de supprimer ce Moment.'
        )
      );
    }
  };

  const toggleMuted = (momentId) => {
    setMutedIds((previous) => {
      const next = new Set(previous);

      if (next.has(momentId)) {
        next.delete(momentId);
      } else {
        next.add(momentId);
      }

      return next;
    });
  };

  const togglePaused = (momentId) => {
    setPausedIds((previous) => {
      const next = new Set(previous);
      const element =
        cardRefs.current.get(momentId);
      const video =
        element?.querySelector('video');

      if (next.has(momentId)) {
        next.delete(momentId);
        video?.play().catch(() => { });
      } else {
        next.add(momentId);
        video?.pause();
      }

      return next;
    });
  };

  if (loading) {
    return (
      <main className="user-moments-page">
        <div className="user-moments-loading">
          <div />
          <p>
            {t(
              'moments.loading',
              'Chargement...'
            )}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="user-moments-page">
      <section className="user-moments-shell">
        <header className="user-moments-header">
          <button
            type="button"
            className="user-moments-back"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
          </button>

          <div className="user-moments-profile">
            <img
              src={
                profile?.userProfilePhoto ||
                '/default-profile.png'
              }
              alt={
                profile?.userName ||
                'User'
              }
            />

            <div>
              <h1>
                {profile?.userName ||
                  t(
                    'moments.userMoments',
                    'Moments'
                  )}
              </h1>

              <p>
                {moments.length}{' '}
                {t(
                  'moments.totalMoments',
                  'Moment(s)'
                )}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="user-moments-create"
            onClick={() =>
              navigate('/moments/create')
            }
          >
            <FaPlus />
          </button>
        </header>

        {errorMessage && (
          <div className="user-moments-error">
            <p>{errorMessage}</p>

            <button
              type="button"
              onClick={loadMoments}
            >
              {t(
                'commons.retry',
                'Réessayer'
              )}
            </button>
          </div>
        )}

        {!errorMessage &&
          moments.length === 0 && (
            <div className="user-moments-empty">
              <h2>
                {t(
                  'moments.noUserMoments',
                  'Aucun Moment disponible'
                )}
              </h2>

              <p>
                {t(
                  'moments.noUserMomentsText',
                  'Cet utilisateur n’a pas encore publié de Moment actif.'
                )}
              </p>
            </div>
          )}

        <div className="user-moments-list">
          {moments.map((moment) => {
            const isVideo =
              moment.mediaType === 'VIDEO' ||
              moment.contentType?.startsWith(
                'video/'
              );

            const isMuted =
              mutedIds.has(moment.id);

            const isPaused =
              pausedIds.has(moment.id);

            return (
              <article
                key={moment.id}
                ref={(element) =>
                  setCardRef(
                    moment.id,
                    element
                  )
                }
                data-moment-id={moment.id}
                className="user-moments-card"
              >
                <div className="user-moments-media">
                  {isVideo ? (
                    <video
                      src={moment.mediaUrl}
                      poster={
                        moment.thumbnailUrl || ''
                      }
                      muted={isMuted}
                      loop
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={moment.mediaUrl}
                      alt={
                        moment.caption ||
                        'Moment'
                      }
                    />
                  )}

                  {isVideo && (
                    <>
                      <button
                        type="button"
                        className="user-moments-control user-moments-control--play"
                        onClick={() =>
                          togglePaused(moment.id)
                        }
                      >
                        {isPaused ? (
                          <FaPlay />
                        ) : (
                          <FaPause />
                        )}
                      </button>

                      <button
                        type="button"
                        className="user-moments-control user-moments-control--sound"
                        onClick={() =>
                          toggleMuted(moment.id)
                        }
                      >
                        {isMuted ? (
                          <FaVolumeMute />
                        ) : (
                          <FaVolumeUp />
                        )}
                      </button>
                    </>
                  )}
                </div>

                <div className="user-moments-content">
                  <div className="user-moments-actions">
                    <button
                      type="button"
                      className={
                        moment.likedByCurrentUser
                          ? 'user-moments-action is-liked'
                          : 'user-moments-action'
                      }
                      onClick={() =>
                        handleLike(moment)
                      }
                    >
                      {moment.likedByCurrentUser ? (
                        <FaHeart />
                      ) : (
                        <FaRegHeart />
                      )}

                      <span>
                        {Number(
                          moment.likesCount
                        ) || 0}
                      </span>
                    </button>

                    <button
                      type="button"
                      className="user-moments-action"
                      onClick={() =>
                        navigate(
                          `/moments/${moment.id}/comments`
                        )
                      }
                    >
                      <FaComment />

                      <span>
                        {Number(
                          moment.commentsCount
                        ) || 0}
                      </span>
                    </button>

                    <div className="user-moments-action user-moments-action--static">
                      <FaEye />

                      <span>
                        {Number(
                          moment.viewsCount
                        ) || 0}
                      </span>
                    </div>

                    {moment.ownedByCurrentUser && (
                      <button
                        type="button"
                        className="user-moments-delete"
                        onClick={() =>
                          handleDelete(moment.id)
                        }
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>

                  {moment.caption && (
                    <p className="user-moments-caption">
                      {moment.caption}
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}

export default UserMoments;